import React, { useCallback, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import PrivateRoute from './PrivateRoute';
import DashboardLayout from './Components/Dashboard/DashboardLayout';
import Login from './Login/Login';
import { AuthProvider } from './Context/AuthContext';
import { DashBoard } from './BackEnd/Dashboard/Dashboard';
import http from './commonFunctions/httpClient';
import { DashboardProvider } from './Context/DashboardContext';
import { useSnackbar } from './Components/Snackbar/SnackBarContext';
import Unauthorized from './BackEnd/Unauthorized';
import QuotationPrint from './BackEnd/Sales/Quotation/QuotationPrint';
import InvoicePrint from './BackEnd/Sales/Invoice/InvoicePrint';

import { ChequeReports, InventoryReports } from "./BackEnd/Reports/Reports";
import { ItemMasterList } from "./BackEnd/Inventory/ItemMaster/ItemMasterList";
import ItemMaster from "./BackEnd/Inventory/ItemMaster/itemMaster";
import { CategoryManagementList } from "./BackEnd/Inventory/CategoryManagement/CategoryManagementList";
import CategoryManagement from "./BackEnd/Inventory/CategoryManagement/CategoryManagement";
import Unit from "./BackEnd/Inventory/UnitManagement/Unit";
import { UnitList } from "./BackEnd/Inventory/UnitManagement/UnitList";
import { SupplierList } from "./BackEnd/Inventory/SupplierManagement/SupplierList";
import Supplier from "./BackEnd/Inventory/SupplierManagement/Supplier";
import Customer from "./BackEnd/Inventory/CustomerManagement/Customer";
import { CustomerList } from "./BackEnd/Inventory/CustomerManagement/CustomerList";
import { OpeningStockList } from "./BackEnd/Inventory/OpeningStock/OpeningStockList";
import OpeningStock from "./BackEnd/Inventory/OpeningStock/OpeningStock";
import StockentryList from "./BackEnd/Inventory/stockEntry/StockentryList";
import StockEntry from "./BackEnd/Inventory/stockEntry/StockEntry";
import { StockAdjustmentList } from "./BackEnd/Inventory/StockAdjustment/StockAdjustmentList";
import StockAdjustment from "./BackEnd/Inventory/StockAdjustment/StockAdjustment";
import Verification from "./BackEnd/Inventory/StockVerification/Verification";
import { VerificationList } from "./BackEnd/Inventory/StockVerification/VerificationList";
import { StockLedgerList } from "./BackEnd/Inventory/StockLedger/StockLedgerList";
import Bank from "./BackEnd/ChequeManagement/BankDetails/Bank";
import { BankList } from "./BackEnd/ChequeManagement/BankDetails/BankList";
import { ChequeStatusList } from "./BackEnd/ChequeManagement/ChequeStatus/ChequeStatusList";
import ChequeStatus from "./BackEnd/ChequeManagement/ChequeStatus/ChequeStatus";
import ChequeEntry from "./BackEnd/ChequeManagement/ChequeEntry/ChequeEntry";
import { UserList } from "./BackEnd/Administration/UserManagement/UserList";
import User from "./BackEnd/Administration/UserManagement/User";
import RolePermission from "./BackEnd/Administration/RolePermission/RolePermission";
import { WarehouseList } from "./BackEnd/Inventory/WareHouse/WarehouseList";
import Warehouse from "./BackEnd/Inventory/WareHouse/Warehouse";
import { BalanceList } from "./BackEnd/Inventory/StockBalance/BalanceList";
import { ChequeEntryList } from "./BackEnd/ChequeManagement/ChequeEntry/ChequeEntryList";
import { ExpenseList } from "./BackEnd/Sales/Expense/ExpenseList";
import Expense from "./BackEnd/Sales/Expense/Expense";
import ProtectedRoute from "./ProtectedRoute";
import InvoiceForm from "./BackEnd/Sales/Invoice/InvoiceForm";
import { InvoiceList } from "./BackEnd/Sales/Invoice/InvoiceList";
import Quotation from "./BackEnd/Sales/Quotation/Quotation";
import { QuotationList } from "./BackEnd/Sales/Quotation/QuotationList";
import { TechnicianList } from "./BackEnd/Administration/Technician/TechnicianList";
import Technician from "./BackEnd/Administration/Technician/Technician";
import StockReturn from './BackEnd/Inventory/StockReturn/StockReturn';
import { StockReturnList } from './BackEnd/Inventory/StockReturn/StockReturnList';
import { BrandList } from './BackEnd/Inventory/Brand/BrandList';
import Brand from './BackEnd/Inventory/Brand/Brand';

interface LogoutResponse {
  message: string;
}

const App: React.FC = () => {
  const { showMessage } = useSnackbar();

  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem("accessToken");
  });

  const handleLogin = (accessToken: string, refreshToken?: string) => {
    setToken(accessToken);
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken || '');
  };

  const handleLogout = useCallback(async () => {
    try {
      const response = await http.post<LogoutResponse>('auth/logout', {
        refreshToken: localStorage.getItem('refreshToken'),
      });

      showMessage(response.message || "Logged out successfully", 'success');

      setToken(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

    } catch (error) {
      console.error('failed to logout', error);
    }
  }, []);

  return (
    <AuthProvider token={token} handleLogin={handleLogin} handleLogout={handleLogout}>
      <DashboardProvider>
        <Router>
          <CssBaseline />

          <Routes>
            <Route
              path="/login"
              element={!token ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
            />

            <Route
              path="/"
              element={token ? <DashboardLayout /> : <Navigate to="/login" />}
            >
              <Route path='/unauthorized' element={<Unauthorized />} />
              <Route element={<PrivateRoute token={token} />}>
              
                {/* Dashboard  & Item Master /*/}
                <Route index element={<DashBoard />} />
                <Route path="/itemmaster/list" element={<ItemMasterList />} />
                <Route path="/itemmaster" element={<ItemMaster />} />
                <Route path="/itemmaster/:id" element={<ItemMaster />} />

                <Route path="/openingstock/itemmaster" element={<ItemMaster />} />
                <Route path="/adjustment/itemmaster" element={<ItemMaster />} />
                <Route path="/verification/itemmaster" element={<ItemMaster />} />
                <Route path="/stock-return/itemmaster" element={<ItemMaster />} />
                <Route path="/stockin/itemmaster" element={<ItemMaster />} />
                <Route path="/stockout/itemmaster" element={<ItemMaster />} />
                <Route path="/quotation/itemmaster" element={<ItemMaster />} />
                <Route path="/invoice/itemmaster" element={<ItemMaster />} />

                {/* Category Management /*/}
                <Route path="/category/list" element={<CategoryManagementList />} />
                <Route path="/category" element={<CategoryManagement />} />
                <Route path="/category/:id" element={<CategoryManagement />} />

                <Route path="/itemmaster/category" element={<CategoryManagement />} />

                {/* Brand /*/}
                <Route path="/brand/list" element={<BrandList />} />
                <Route path="/brand" element={<Brand />} />
                <Route path="/brand/:id" element={<Brand />} />


                <Route path="/itemmaster/brand" element={<Brand />} />

                {/* Unit Management /*/}
                <Route path="/unit/list" element={<UnitList />} />
                <Route path="/unit" element={<Unit />} />
                <Route path="/unit/:id" element={<Unit />} />

                <Route path="/itemmaster/unit" element={<Unit />} />

                {/* Supplier Management /*/}
                <Route path="/supplier/list" element={<SupplierList />} />
                <Route path="/supplier" element={<Supplier />} />
                <Route path="/supplier/:id" element={<Supplier />} />

                <Route path="/itemmaster/supplier" element={<Supplier />} />

                {/* Customer Management /*/}
                <Route path="/customer/list" element={<CustomerList />} />
                <Route path="/customer" element={<Customer />} />
                <Route path="/customer/:id" element={<Customer />} />

                <Route path="/stockout/customer" element={<Customer />} />
                <Route path="/quotation/customer" element={<Customer />} />
                <Route path="/invoice/customer" element={<Customer />} />

                {/* Warehouse /*/}
                <Route path="/warehouse/list" element={<WarehouseList />} />
                <Route path="/warehouse" element={<Warehouse />} />
                <Route path="/warehouse/:id" element={<Warehouse />} />


                <Route path="/openingstock/warehouse" element={<Warehouse />} />
                <Route path="/adjustment/warehouse" element={<Warehouse />} />
                <Route path="/verification/warehouse" element={<Warehouse />} />
                <Route path="/stock-return/warehouse" element={<Warehouse />} />
                <Route path="/itemmaster/warehouse" element={<Warehouse />} />
                <Route path="/stockin/warehouse" element={<Warehouse />} />
                <Route path="/stockout/warehouse" element={<Warehouse />} />

                {/* Opening Stock /*/}
                <Route path="/openingstock/list" element={<OpeningStockList />} />
                <Route path="/openingstock" element={<OpeningStock />} />
                <Route path="/openingstock/:id" element={<OpeningStock />} />

                {/* Stock Entry /*/}
                <Route path="/stockentry/list" element={<StockentryList />} />
                <Route path="/stockin" element={<StockEntry />} />
                <Route path="/stockin/:id" element={<StockEntry />} />
                <Route path="/stockout" element={<StockEntry />} />
                <Route path="/stockout/:id" element={<StockEntry />} />

                {/* <Route path="/stock-balance/list" element={<BalanceList />} /> */}
                <Route path="/stockledger" element={<StockLedgerList />} />

                {/* Adjustment /*/}
                <Route path="/adjustment/list" element={<StockAdjustmentList />} />
                <Route path="/adjustment" element={<StockAdjustment />} />

                {/* Verification /*/}
                <Route path="/verification/list" element={<VerificationList />} />
                <Route path="/verification" element={<Verification />} />

                {/* Bank Management /*/}
                <Route path="/bank/list" element={<BankList />} />
                <Route path="/bank" element={<Bank />} />
                <Route path="/bank/:id" element={<Bank />} />

                <Route path="/chequeentry/bank" element={<Bank />} />

                {/* Cheque Management /*/}
                <Route path="/chequestatus/list" element={<ChequeStatusList />} />
                <Route path="/chequestatus" element={<ChequeStatus />} />
                <Route path="/chequeentry" element={<ChequeEntry />} />
                <Route path="/chequeentry/list" element={<ChequeEntryList />} />

                

                 {/* Stock Return /*/}
                <Route path="/stock-return/list" element={<StockReturnList />} />
                <Route path="/stock-return" element={<StockReturn />} />
                <Route path="/stock-return/:id" element={<StockReturn />} />

                {/* User Management /*/}
                <Route path="/user/list" element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <UserList />
                  </ProtectedRoute>
                } />
                <Route path="/user" element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <User />
                  </ProtectedRoute>
                } />
                <Route path="/user/:id" element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <User />
                  </ProtectedRoute>
                } />

                {/* Role Permission /*/}
                <Route path="/role-permission" element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <RolePermission />
                  </ProtectedRoute>
                } />

                {/* Reports /*/}
                <Route path="/inventory/reports" element={<ProtectedRoute allowedRoles={["ADMIN"]}><InventoryReports /></ProtectedRoute>} />
                <Route path="/cheque/reports" element={<ProtectedRoute allowedRoles={["ADMIN"]}><ChequeReports /></ProtectedRoute>} />

                {/* Expense /*/}
                <Route path="/expense/list" element={<ExpenseList />} />
                <Route path="/expense" element={<Expense />} />
                <Route path="/expense/:id" element={<Expense />} />

                {/* Quotation /*/}
                <Route path="/quotation/list" element={<QuotationList />} />
                <Route path="/quotation" element={<Quotation />} />
                <Route path="/quotation/:id" element={<Quotation />} />


                {/* Invoice /*/}
                <Route path="/invoice/list" element={<InvoiceList />} />
                <Route path="/invoice" element={<InvoiceForm />} />
                <Route path="/invoice/:id" element={<InvoiceForm />} />

                {/* Technician /*/}
                <Route path="/technician/list" element={<TechnicianList />} />
                <Route path="/technician" element={<Technician />} />
                <Route path="/technician/:id" element={<Technician />} />
                <Route path="/stockout/technician" element={<Technician />} />
              </Route>
            </Route>

            {/* print  */}
            <Route path="/quotation/print/:quotationId"
              element={
                token ? <QuotationPrint /> : <Navigate to="/login" />
              } />
            <Route
              path="/invoice/print/:invoiceId"
              element={
                token ? <InvoicePrint /> : <Navigate to="/login" />
              }
            />
          </Routes>
        </Router>
      </DashboardProvider>
    </AuthProvider>
  );
};

export default App;